'use client';

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { adoptionInquiryService } from '@/services/adoptionInquiryService';
import { applicationDocumentService, type ApplicationDocument } from '@/services/applicationDocumentService';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

interface Application {
  id: string;
  petName: string;
  petImage: string;
  status: 'pending' | 'reviewing' | 'approved' | 'rejected' | 'completed';
  submittedAt: string;
  contactEmail: string;
  interviewScheduledAt?: string;
  interviewWhatsappLink?: string;
  rejectionReason?: string;
  adminReviewedAt?: string;
  paymentStatus?: string;
  documents: ApplicationDocument[];
}

export default function ApplicationStatusTrackingPage() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const loadApplications = useCallback(async () => {
    if (!user) return;

    try {
      const inquiries = await adoptionInquiryService.getByUserId(user.id);

      const appsWithDocs = await Promise.all(
        inquiries.map(async (inquiry: any) => {
          try {
            let docs: ApplicationDocument[] = [];
            if (inquiry.id) {
              const response = await applicationDocumentService.getInquiryDocuments(inquiry.id);
              docs = response.data || [];
            }
            return {
              id: inquiry.id,
              petName: inquiry.pet?.name || 'Unknown Pet',
              petImage: inquiry.pet?.imageUrl || '/assets/images/no_image.png',
              status: inquiry.status,
              submittedAt: inquiry.submittedAt || inquiry.createdAt,
              contactEmail: inquiry.contactEmail,
              interviewScheduledAt: inquiry.interviewScheduledAt,
              interviewWhatsappLink: inquiry.interviewWhatsappLink,
              rejectionReason: inquiry.rejectionReason,
              adminReviewedAt: inquiry.adminReviewedAt,
              paymentStatus: inquiry.paymentStatus,
              documents: docs || []
            };
          } catch (docError) {
            console.error(`Error loading documents for inquiry ${inquiry.id}:`, docError);
            return {
              id: inquiry.id,
              petName: inquiry.pet?.name || 'Unknown Pet',
              petImage: inquiry.pet?.imageUrl || '/assets/images/no_image.png',
              status: inquiry.status,
              submittedAt: inquiry.submittedAt || inquiry.createdAt,
              contactEmail: inquiry.contactEmail,
              interviewScheduledAt: inquiry.interviewScheduledAt,
              interviewWhatsappLink: inquiry.interviewWhatsappLink,
              rejectionReason: inquiry.rejectionReason,
              adminReviewedAt: inquiry.adminReviewedAt,
              paymentStatus: inquiry.paymentStatus,
              documents: []
            };
          }
        })
      );
      setApplications(appsWithDocs);
    } catch (err) {
      console.error('Error loading applications:', err);
      setError(err instanceof Error ? err.message : 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!mounted) return;

    if (!authLoading && !user) {
      router.push('/login');
      return;
    }

    if (user) {
      loadApplications();
    }
  }, [user, authLoading, router, mounted, loadApplications]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'reviewing':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending Review';
      case 'reviewing':
        return 'Under Review';
      case 'approved':
        return 'Approved - Action Required';
      case 'rejected':
        return 'Not Approved';
      case 'completed':
        return 'Completed';
      default:
        return status;
    }
  };

  const getProgressSteps = (app: Application) => {
    const steps = [
      { label: 'Application Submitted', completed: !!app.submittedAt },
      { label: 'Under Review', completed: app.status !== 'pending' },
      { label: 'Interview Scheduled', completed: !!app.interviewScheduledAt },
      { label: 'Payment & Completion', completed: app.status === 'completed' }
    ];
    return steps;
  };

  if (!mounted || authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Adoption Applications</h1>
          <p className="text-gray-600 mt-2">Track the status of your adoption applications</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {applications.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Applications Yet</h3>
            <p className="text-gray-600 mb-6">You have not submitted any adoption applications yet.</p>
            <button
              onClick={() => router.push('/pet-discovery')}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-secondary"
            >
              Browse Available Pets
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {applications.map((app) => (
              <div key={app.id} className="bg-white rounded-xl shadow-md overflow-hidden">
                {/* Pet Info Header */}
                <div className="flex items-center gap-4 p-6 bg-gradient-to-r from-blue-50 to-blue-100">
                  <img
                    src={app.petImage}
                    alt={app.petName}
                    className="w-20 h-20 rounded-lg object-cover"
                  />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{app.petName}</h3>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(app.status)}`}>
                      {getStatusText(app.status)}
                    </span>
                  </div>
                </div>

                {/* Application Timeline */}
                <div className="p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Application Progress</h4>
                  <div className="space-y-4">
                    {getProgressSteps(app).map((step, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${step.completed ? 'bg-green-500' : 'bg-gray-300'
                          }`}>
                          {step.completed && (
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${step.completed ? 'text-gray-900' : 'text-gray-500'}`}>
                            {step.label}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Status-specific information */}
                  {app.status === 'approved' && app.interviewWhatsappLink && (
                    <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h5 className="font-semibold text-green-900 mb-2">Interview Scheduled!</h5>
                      <p className="text-sm text-green-800 mb-3">
                        {app.interviewScheduledAt ? `Scheduled for: ${new Date(app.interviewScheduledAt).toLocaleString()}` : 'Time to be confirmed'}
                      </p>
                      <a
                        href={app.interviewWhatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        Join WhatsApp Interview
                      </a>
                    </div>
                  )}

                  {app.status === 'rejected' && app.rejectionReason && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h5 className="font-semibold text-red-900 mb-2">Application Status</h5>
                      <p className="text-sm text-red-800">{app.rejectionReason}</p>
                    </div>
                  )}

                  {/* Documents Section */}
                  <div className="mt-6 border-t border-gray-200 pt-4">
                    <h5 className="font-semibold text-gray-900 mb-3">Uploaded Documents</h5>
                    {!app.documents || app.documents.length === 0 ? (
                      <p className="text-sm text-gray-500">No documents uploaded yet</p>
                    ) : (
                      <div className="space-y-2">
                        {(app.documents || []).map((doc: any) => (
                          <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              <span className="text-sm text-gray-700">
                                {applicationDocumentService.getDocumentTypeLabel(doc.documentType)}
                              </span>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full ${doc.status === 'verified' ? 'bg-green-100 text-green-800' :
                              doc.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                              {doc.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 flex gap-3">
                    <Link
                      href={`/application-status-tracking/${app.id}`}
                      className="flex-1 px-4 py-2 text-primary border border-primary rounded-lg hover:bg-blue-50 font-medium text-center"
                    >
                      View Details
                    </Link>
                    {app.status === 'pending' && (
                      <button
                        onClick={() => router.push(`/adoption-application?petId=${app.id}`)}
                        className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary font-medium"
                      >
                        Update Application
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}


      </div>
    </div>
  );
}